/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/auth/RequireAuth.tsx
 * PURPOSE: Authentication-only route guard — blocks anonymous access to routes whose
 *          backend requires a valid session but NO specific role
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-07-12
 * COMPLIANCE: SDLC 6.3.1 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Frontend Authorization, Vietnamese Multi-Industry BPM
 * - Impact: P1 — closes anonymous-access gap on routes with no role requirement
 * - Stakeholders: All authenticated roles, Security Team
 *
 * @technical-architecture
 * - Layer: UI Component (Authorization Guard)
 * - Pattern: HOC wrapping route children — sibling of RequireRole
 * - Dependencies: React, react-router-dom, zustand (auth store)
 *
 * @cross-references
 * - Sibling: frontend/src/components/auth/RequireRole.tsx (role-tier guard)
 * - Store: frontend/src/store/auth.ts (useAuthStore)
 * - Router: frontend/src/App.tsx (protected routes)
 * - Backend: backend/app/api/v1/workflows.py (get_current_user, no role dependency)
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/auth";

interface RequireAuthProps {
  /** Content to render when authenticated */
  children: React.ReactNode;
}

/**
 * RequireAuth — Authentication-only route guard.
 *
 * Use this (rather than RequireRole) when the backend endpoints behind a route
 * require a valid session but deliberately enforce NO role restriction — i.e. the
 * correct access level is "any authenticated user". Encoding that as RequireRole
 * with all 14 TenantUserRole values would be brittle (a newly-added role would be
 * silently locked out) and would misrepresent the backend's actual contract.
 *
 * Behavior (mirrors RequireRole minus the role check):
 *   - Renders null until auth state is loaded (no flash of wrong content)
 *   - If not authenticated → redirect to /login, preserving `from` location
 *   - Otherwise → render children
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  // Wait for auth state to load — render nothing (no flash)
  if (isLoading) {
    return null;
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
